import { FC, ReactNode } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';

import { DeniedPaymentAuthorizationError } from './constants';

type Props = {
    status: string;
    helpTextClass?: string;
};

const DeniedPaymentAuthorizationMessage: FC<Props> = ({ status, helpTextClass }) => {
    const deniedPaymentStatusMap: {
        [key in DeniedPaymentAuthorizationError]: ReactNode;
    } = {
        FAILURE_BAD_PAYMENT_METHOD: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_BAD_PAYMENT_METHOD"
                defaultMessage="1stDibs was unable to process this order due to a technical problem. <span>Please use a different payment method for this order.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_BANK_SOFT_DECLINE: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_BANK_SOFT_DECLINE"
                defaultMessage="1stDibs was unable to process this order. <span>It is recommended that you contact your bank to alert them of the purchase and try again. If it still doesn’t work, please use a different payment method.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_CARD_ERROR: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_CARD_ERROR"
                defaultMessage="1stDibs was unable to process this order. <span>Please check your credit card information and try again. If it still doesn’t work, please use a different payment method.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_CC_EXPIRATION: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_CC_EXPIRATION"
                defaultMessage="1stDibs was unable to process this order because the credit card has expired. <span>Please use a different payment method for this order.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_LIMIT_EXCEEDED: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_LIMIT_EXCEEDED"
                defaultMessage="1stDibs was unable to process this order because the total amount either exceeds your available funds or the daily limit on transactions. <span>Please contact your bank to alert them of this purchase and try again. If it still doesn’t work, please use a different payment method.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_PAYPAL_ACCOUNT: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_PAYPAL_ACCOUNT"
                defaultMessage="1stDibs was unable to process this order because of an issue with your PayPal account. <span>Please correct any errors on your PayPal profile and submit the order again.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_PAYPAL_DECLINE: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_PAYPAL_DECLINE"
                defaultMessage="1stDibs was unable to process this order because the payment method associated with your PayPal account was declined. <span>Please update your PayPal payment method and try again.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_PAYPAL_SUPPORT: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_PAYPAL_SUPPORT"
                defaultMessage="1stDibs was unable to process this order with your PayPal account. <span>We suggest that you contact PayPal customer support and submit the order again.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        FAILURE_RETRY: (
            <FormattedMessage
                id="deniedPaymentAuthorization.FAILURE_RETRY"
                defaultMessage="1stDibs was unable to process this order due to a technical problem. <span>Feel free to submit your order again. If the problem continues, use a different payment method for this order.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        PROCESSOR_ERROR: (
            <FormattedMessage
                id="deniedPaymentAuthorization.PROCESSOR_ERROR"
                defaultMessage="<span>1stDibs was unable to process this order.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
        SERVICE_ERROR: (
            <FormattedMessage
                id="deniedPaymentAuthorization.SERVICE_ERROR"
                defaultMessage="1stDibs was unable to process this order due to a technical problem with our payment process. <span>Please resubmit the order. If it still doesn’t work, please use a different payment method.</span>"
                values={{
                    span: content => <span className={classnames(helpTextClass)}>{content}</span>,
                }}
            />
        ),
    };

    const deniedPaymentMessage = deniedPaymentStatusMap[status as DeniedPaymentAuthorizationError];

    if (!deniedPaymentMessage) {
        return null;
    }

    return <span>{deniedPaymentMessage}</span>;
};

export default DeniedPaymentAuthorizationMessage;
